import * as React from 'react';

type Block = {
  id: 'a' | 'b' | 'c' | 'd';
  d: string;
  cx: number;
  cy: number;
  grad: string;
  role: string;
};

const PATH_A =
  'M4.95434 3.38919C2.12842 3.72466 0 6.1207 0 8.96647V40.3873L10.3444 37.3449C12.8966 36.5943 14.6492 34.252 14.6492 31.5917V2.23828L4.95434 3.38919Z';
const PATH_B =
  'M33.079 0L18.9024 1.71863V26.795L28.8395 23.7493C31.3589 22.9771 33.079 20.6508 33.079 18.0158V0Z';
const PATH_C =
  'M20.9213 55.8984L35.0979 54.1798V29.1034L25.1608 32.1491C22.6414 32.9213 20.9213 35.2476 20.9213 37.8827V55.8984Z';
const PATH_D =
  'M49.0457 52.5092C51.8716 52.1738 54 49.7777 54 46.932V15.5112L43.6556 18.5535C41.1034 19.3042 39.3508 21.6464 39.3508 24.3067V53.6602L49.0457 52.5092Z';

const BLOCKS: Block[] = [
  { id: 'a', d: PATH_A, cx: 7.32, cy: 21.3, grad: 'g-tall-left', role: 'tall' },
  { id: 'b', d: PATH_B, cx: 25.99, cy: 13.4, grad: 'g-inset-top', role: 'inset-top' },
  { id: 'c', d: PATH_C, cx: 28.01, cy: 42.5, grad: 'g-inset-bot', role: 'inset-bot' },
  { id: 'd', d: PATH_D, cx: 46.68, cy: 34.6, grad: 'g-tall-right', role: 'tall' },
];

const TEAL_DEEP = '#2D6366';
const TEAL_MID = '#498D91';

type PvXMarkProps = {
  size?: number;
  className?: string;
  style?: React.CSSProperties;
  mode?: 'brand' | 'flat' | 'mono';
  monoColor?: string;
  blockStyle?: (b: Block, index: number) => React.CSSProperties;
  blockClass?: (b: Block, index: number) => string;
} & React.SVGProps<SVGSVGElement>;

function PvXMark({
  size = 120,
  className = '',
  style = {},
  mode = 'brand',
  monoColor = '#fff',
  blockStyle = () => ({}),
  blockClass = () => '',
  ...svgProps
}: PvXMarkProps) {
  const uid = React.useId().replace(/:/g, '');

  return (
    <svg
      viewBox="0 0 54 56"
      width={size}
      height={size * (56 / 54)}
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={{ overflow: 'visible', ...style }}
      {...svgProps}
    >
      <defs>
        <linearGradient
          id={`g-tall-left-${uid}`}
          x1="0"
          y1="34.63"
          x2="85.52"
          y2="-14.64"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor={TEAL_DEEP} />
          <stop offset="1" stopColor="#ffffff" />
        </linearGradient>
        <linearGradient
          id={`g-inset-top-${uid}`}
          x1="18.9"
          y1="30"
          x2="60"
          y2="0"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor={TEAL_DEEP} />
          <stop offset="1" stopColor="#ffffff" />
        </linearGradient>
        <linearGradient
          id={`g-tall-right-${uid}`}
          x1="10"
          y1="55.88"
          x2="133"
          y2="7.36"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor={TEAL_DEEP} />
          <stop offset="1" stopColor="#ffffff" />
        </linearGradient>
        <linearGradient
          id={`g-inset-bot-${uid}`}
          x1="20.92"
          y1="55.9"
          x2="54"
          y2="25"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor={TEAL_MID} />
          <stop offset="1" stopColor="#ffffff" />
        </linearGradient>
      </defs>

      {BLOCKS.map((b, i) => {
        const gradUrl = `url(#${b.grad}-${uid})`;
        const fill = mode === 'mono' ? monoColor : mode === 'flat' ? TEAL_MID : gradUrl;

        return (
          <path
            key={b.id}
            data-block={b.id}
            data-role={b.role}
            d={b.d}
            fill={fill}
            className={blockClass(b, i)}
            style={{
              transformOrigin: `${b.cx}px ${b.cy}px`,
              transformBox: 'view-box',
              ...blockStyle(b, i),
            }}
          />
        );
      })}
    </svg>
  );
}

export default React.memo(PvXMark);
